import { useEffect, useState } from "react";
import "@/App.css";
import axios from "axios";
import {
  Menu,
  X,
  ArrowRight,
  Car,
  QrCode,
  MapPin,
  Smartphone,
  Users,
  BarChart3,
  ShieldCheck,
  Camera,
  Radio,
  CreditCard,
  Gauge,
  Building2,
  Hospital,
  ShoppingBag,
  Plane,
  UtensilsCrossed,
  CalendarDays,
  Check,
  Phone,
  Mail,
  MapPinned,
  Clock,
  Sparkles,
  ChevronRight,
  Star,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ------------------------------------------------------------------ */
/*  Reusable chrome                                                    */
/* ------------------------------------------------------------------ */

const Header = ({ onDemoClick }) => {
  const [open, setOpen] = useState(false);
  const links = [
    { id: "solutions", label: "Solutions" },
    { id: "software", label: "Software" },
    { id: "hardware", label: "Hardware" },
    { id: "manpower", label: "Manpower" },
    { id: "industries", label: "Industries" },
    { id: "contact", label: "Contact" },
  ];
  return (
    <header className="pb-glass sticky top-0 z-50" data-testid="site-header">
      <div className="mx-auto max-w-7xl px-6 md:px-10 flex items-center justify-between h-16">
        <a
          href="#top"
          className="flex items-center group"
          data-testid="brand-logo"
        >
          <img
            src="/parking-boss-logo.png"
            alt="Parking Boss"
            className="h-11 w-auto object-contain"
          />
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              data-testid={`nav-link-${l.id}`}
              className="text-sm text-zinc-400 hover:text-white transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:block">
          <button
            onClick={onDemoClick}
            className="pb-btn-primary text-sm"
            data-testid="header-demo-button"
          >
            Request Demo <ArrowRight size={16} />
          </button>
        </div>

        <button
          className="md:hidden text-white"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div
          className="md:hidden border-t border-white/10 bg-[#050505]"
          data-testid="mobile-menu"
        >
          <div className="px-6 py-4 flex flex-col gap-4">
            {links.map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${l.id}`}
                className="text-zinc-300"
              >
                {l.label}
              </a>
            ))}
            <button
              onClick={() => {
                setOpen(false);
                onDemoClick();
              }}
              className="pb-btn-primary justify-center"
              data-testid="mobile-demo-button"
            >
              Request Demo
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

/* ------------------------------------------------------------------ */
/*  Sections                                                           */
/* ------------------------------------------------------------------ */

const Hero = ({ onDemoClick, stats }) => {
  return (
    <section
      id="top"
      className="relative isolate overflow-hidden pb-noise"
      data-testid="hero-section"
    >
      <div
        className="absolute inset-0 -z-10"
        style={{
          backgroundImage:
            "url(https://images.pexels.com/photos/35458674/pexels-photo-35458674.jpeg)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#050505]/70 via-[#050505]/80 to-[#050505]" />

      <div className="mx-auto max-w-7xl px-6 md:px-10 pt-20 md:pt-28 pb-24 md:pb-36 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-8 pb-rise">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/5 backdrop-blur text-xs tracking-[0.18em] uppercase">
            <Sparkles size={14} className="text-[#D4AF37]" />
            <span className="text-zinc-300">
              Smart Valet · Hardware · Manpower
            </span>
          </div>

          <h1 className="font-display mt-6 text-4xl sm:text-5xl lg:text-7xl leading-[1.02] font-light">
            The valet experience,{" "}
            <span className="pb-gold-text font-medium">reinvented</span> for
            modern venues.
          </h1>

          <p className="mt-6 max-w-2xl text-zinc-400 text-base md:text-lg leading-relaxed font-light">
            Parking Boss unifies valet software, smart parking hardware and
            trained manpower into one operating system — so hotels, malls,
            hospitals and airports deliver a flawless arrival, every time.
          </p>

          <div className="mt-10 flex flex-wrap gap-3">
            <button
              onClick={onDemoClick}
              className="pb-btn-primary"
              data-testid="hero-primary-cta"
            >
              Request a Demo <ArrowRight size={18} />
            </button>
            <a
              href="#solutions"
              className="pb-btn-secondary"
              data-testid="hero-secondary-cta"
            >
              Explore Solutions <ChevronRight size={18} />
            </a>
          </div>

          <div className="mt-12 flex items-center gap-4 text-xs text-zinc-500">
            <div className="flex -space-x-2">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-8 w-8 rounded-full border border-white/10 bg-zinc-800"
                  style={{
                    backgroundImage: `url(https://i.pravatar.cc/64?img=${i + 12})`,
                    backgroundSize: "cover",
                  }}
                />
              ))}
            </div>
            <span className="uppercase tracking-[0.2em]">
              Trusted by 180+ premium venues
            </span>
          </div>
        </div>

        <div className="lg:col-span-4 pb-rise" style={{ animationDelay: "200ms" }}>
          <div className="pb-card p-6 backdrop-blur-xl bg-[#0D1117]/80">
            <div className="pb-eyebrow">Live · Demo Ticket</div>
            <div className="mt-4 flex items-start justify-between">
              <div>
                <div className="text-zinc-500 text-xs">Ticket</div>
                <div className="font-display text-2xl">#A-9032</div>
              </div>
              <QrCode className="text-[#D4AF37]" size={56} strokeWidth={1.2} />
            </div>
            <div className="mt-5 grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-zinc-500 text-xs">Vehicle</div>
                <div>Range Rover · MH01 GX 4421</div>
              </div>
              <div>
                <div className="text-zinc-500 text-xs">Valet</div>
                <div>Arjun R.</div>
              </div>
              <div>
                <div className="text-zinc-500 text-xs">Bay</div>
                <div>B3 · Slot 214</div>
              </div>
              <div>
                <div className="text-zinc-500 text-xs">Wait ETA</div>
                <div className="pb-gold-text">2 min 40s</div>
              </div>
            </div>
            <button
              className="mt-6 w-full pb-btn-primary justify-center"
              data-testid="hero-demo-request-car"
            >
              Request my car
            </button>
          </div>
        </div>
      </div>

      {/* Stats strip */}
      <div className="relative border-y border-white/10 bg-[#050505]/70 backdrop-blur">
        <div className="mx-auto max-w-7xl px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-6 py-8">
          {[
            {
              v: stats ? `${(stats.vehicles_managed / 1_000_000).toFixed(1)}M+` : "2.4M+",
              l: "Vehicles managed",
            },
            {
              v: stats ? `${stats.venues_served}+` : "180+",
              l: "Venues served",
            },
            {
              v: stats ? `${Math.round(stats.avg_retrieval_seconds / 60)} min` : "3 min",
              l: "Avg. retrieval time",
            },
            {
              v: stats ? `${stats.uptime_percent}%` : "99.98%",
              l: "Platform uptime",
            },
          ].map((s) => (
            <div key={s.l} data-testid={`stat-${s.l.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="font-display text-2xl md:text-3xl font-medium pb-gold-text">
                {s.v}
              </div>
              <div className="text-xs tracking-[0.2em] uppercase text-zinc-500 mt-1">
                {s.l}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ----- Solutions bento grid ----- */

const SolutionCard = ({ span, title, desc, icon: Icon, bg, children, testid }) => {
  return (
    <div
      className={`pb-card relative overflow-hidden ${span} p-8 min-h-[240px]`}
      data-testid={testid}
      style={
        bg
          ? {
              backgroundImage: `linear-gradient(135deg, rgba(5,5,5,0.75), rgba(13,17,23,0.95)), url(${bg})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }
          : undefined
      }
    >
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
          <Icon size={18} className="text-[#D4AF37]" />
        </div>
        <div className="pb-eyebrow">{title}</div>
      </div>
      <p className="mt-4 text-white text-xl font-display font-light leading-snug max-w-lg">
        {desc}
      </p>
      {children}
    </div>
  );
};

const Solutions = () => {
  return (
    <section
      id="solutions"
      className="relative py-24 md:py-32"
      data-testid="solutions-section"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="max-w-3xl">
          <div className="pb-eyebrow">Solutions</div>
          <h2 className="font-display mt-3 text-3xl md:text-5xl font-light leading-tight">
            A complete operating system for{" "}
            <span className="pb-gold-text font-medium">parking & arrivals</span>.
          </h2>
          <p className="mt-4 text-zinc-400 max-w-2xl">
            Five integrated modules. One unified dashboard. Deployed in under
            two weeks at hotels, malls and hospitals across 12 countries.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-6">
          <SolutionCard
            span="md:col-span-7"
            title="QR Ticketless Valet"
            desc="Drivers scan a QR, get an SMS link, and walk away. Zero paper, zero lost tickets, zero friction on arrival."
            icon={QrCode}
            testid="solution-qr-ticketless"
          >
            <div className="mt-8 flex items-end gap-6">
              <div className="flex-1 h-px bg-white/10" />
              <div className="text-[11px] tracking-[0.24em] uppercase text-zinc-500">
                2s check-in
              </div>
            </div>
          </SolutionCard>

          <SolutionCard
            span="md:col-span-5"
            title="Real-time Tracking"
            desc="Every vehicle's location, status and handler — live on one map."
            icon={MapPin}
            testid="solution-tracking"
          />

          <SolutionCard
            span="md:col-span-5"
            title="Customer Portal"
            desc="Guests request their car from their phone before they reach the lobby."
            icon={Smartphone}
            testid="solution-customer-portal"
          />

          <SolutionCard
            span="md:col-span-7"
            title="Shift & Manpower"
            desc="Schedule valets, track attendance, monitor performance SLAs and tips — from one roster."
            icon={Users}
            testid="solution-shift-manpower"
          >
            <div className="mt-8 flex flex-wrap gap-2 text-xs">
              {["Attendance", "Payroll-ready", "Performance SLAs", "Tips tracking"].map(
                (t) => (
                  <span
                    key={t}
                    className="px-3 py-1 border border-white/10 rounded-full text-zinc-400"
                  >
                    {t}
                  </span>
                ),
              )}
            </div>
          </SolutionCard>

          <SolutionCard
            span="md:col-span-12"
            title="Analytics & Revenue"
            desc="Occupancy heatmaps, retrieval-time SLAs, revenue-per-bay and guest NPS — in one C-suite dashboard."
            icon={BarChart3}
            testid="solution-analytics"
          >
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              {[
                { k: "Avg retrieval", v: "2m 48s" },
                { k: "Occupancy", v: "87%" },
                { k: "Rev / bay / day", v: "₹1,240" },
                { k: "Guest NPS", v: "72" },
              ].map((c) => (
                <div
                  key={c.k}
                  className="border border-white/10 rounded-lg p-4 bg-black/30"
                >
                  <div className="text-xs tracking-[0.2em] uppercase text-zinc-500">
                    {c.k}
                  </div>
                  <div className="font-display text-xl mt-1 pb-gold-text">
                    {c.v}
                  </div>
                </div>
              ))}
            </div>
          </SolutionCard>
        </div>
      </div>
    </section>
  );
};

/* ----- Software ----- */

const SoftwareSection = ({ onDemoClick }) => {
  const features = [
    {
      icon: Gauge,
      t: "Live ops dashboard",
      d: "See every bay, valet and ticket across all sites in real time.",
    },
    {
      icon: ShieldCheck,
      t: "Damage & liability",
      d: "Photo-capture at check-in protects your venue and your guests.",
    },
    {
      icon: CreditCard,
      t: "Integrated billing",
      d: "PMS/POS integrations for hotels, malls and hospitals.",
    },
    {
      icon: Clock,
      t: "Pre-arrival requests",
      d: "Guests ping their car minutes before lobby. No wait.",
    },
  ];

  return (
    <section
      id="software"
      className="relative py-24 md:py-32 border-t border-white/10"
      data-testid="software-section"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6 order-2 lg:order-1">
          <div className="relative pb-card overflow-hidden p-3">
            <img
              src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1400&q=80"
              alt="Parking Boss analytics dashboard"
              className="w-full h-auto rounded-lg"
            />
            <div className="absolute bottom-6 left-6 right-6 flex flex-wrap gap-3">
              <div className="px-3 py-2 rounded-lg bg-black/70 backdrop-blur border border-white/10 text-xs">
                <span className="pb-gold-text">●</span> 412 active tickets
              </div>
              <div className="px-3 py-2 rounded-lg bg-black/70 backdrop-blur border border-white/10 text-xs">
                SLA met: <span className="pb-gold-text">96.4%</span>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-6 order-1 lg:order-2">
          <div className="pb-eyebrow">Software Platform</div>
          <h2 className="font-display mt-3 text-3xl md:text-5xl font-light leading-tight">
            Command center for every{" "}
            <span className="pb-gold-text font-medium">arrival & exit</span>.
          </h2>
          <p className="mt-4 text-zinc-400">
            Built for multi-site operators. Role-based access for GMs, ops
            managers, valets and finance teams. White-label ready.
          </p>

          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            {features.map((f) => (
              <div
                key={f.t}
                className="pb-card p-5"
                data-testid={`software-feature-${f.t.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <f.icon size={18} className="text-[#D4AF37]" />
                <div className="mt-3 font-medium">{f.t}</div>
                <div className="text-sm text-zinc-400 mt-1">{f.d}</div>
              </div>
            ))}
          </div>

          <button
            onClick={onDemoClick}
            className="mt-8 pb-btn-secondary"
            data-testid="software-cta"
          >
            See the dashboard <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </section>
  );
};

/* ----- Hardware ----- */

const HardwareSection = () => {
  const items = [
    { icon: Camera, t: "ANPR Cameras", d: "Plate-capture at 50+ km/h with 99.2% accuracy." },
    { icon: Radio, t: "Boom Barriers", d: "Industrial-grade gates with loop + ANPR triggers." },
    { icon: QrCode, t: "Ticket Dispensers", d: "Paper + QR ticketing with backup fallback." },
    { icon: CreditCard, t: "Payment Kiosks", d: "Card, UPI, wallet and ticket-less exit billing." },
    { icon: ShieldCheck, t: "LPR Gates", d: "Frictionless entry for monthly pass holders." },
    { icon: Gauge, t: "Bay Sensors", d: "Magnetic + ultrasonic bay detection, live occupancy." },
  ];

  return (
    <section
      id="hardware"
      className="relative py-24 md:py-32 border-t border-white/10 pb-grid-bg"
      data-testid="hardware-section"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="max-w-3xl">
          <div className="pb-eyebrow">Hardware</div>
          <h2 className="font-display mt-3 text-3xl md:text-5xl font-light leading-tight">
            Infrastructure that <span className="pb-gold-text font-medium">just works</span>.
          </h2>
          <p className="mt-4 text-zinc-400 max-w-2xl">
            From ANPR cameras to boom barriers, we supply, install and service
            every piece of hardware — certified for 24×7 mission-critical sites.
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-12 gap-6">
          <div className="md:col-span-5 relative pb-card overflow-hidden min-h-[360px]">
            <img
              src="https://images.pexels.com/photos/34182544/pexels-photo-34182544.jpeg"
              alt="Smart parking boom barrier"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <div className="pb-eyebrow">Certified Stack</div>
              <div className="font-display text-2xl mt-2">
                IP66 · PoE · ONVIF compliant
              </div>
            </div>
          </div>

          <div className="md:col-span-7 grid sm:grid-cols-2 gap-4">
            {items.map((h) => (
              <div
                key={h.t}
                className="pb-card p-5"
                data-testid={`hardware-item-${h.t.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="flex items-center justify-between">
                  <h.icon size={20} className="text-[#D4AF37]" />
                  <span className="text-[10px] tracking-[0.22em] uppercase text-zinc-600">
                    In stock
                  </span>
                </div>
                <div className="mt-4 font-medium">{h.t}</div>
                <div className="text-sm text-zinc-400 mt-1">{h.d}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ----- Manpower ----- */

const ManpowerSection = () => {
  const perks = [
    "Hospitality-trained attendants",
    "Background verified & insured",
    "Grooming & uniform SLAs",
    "Multi-lingual staff (EN / HI / regional)",
    "24×7 rostering & backup",
    "Dedicated site supervisor",
  ];
  return (
    <section
      id="manpower"
      className="relative py-24 md:py-32 border-t border-white/10"
      data-testid="manpower-section"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <div className="pb-eyebrow">Manpower Management</div>
          <h2 className="font-display mt-3 text-3xl md:text-5xl font-light leading-tight">
            Valets your guests will{" "}
            <span className="pb-gold-text font-medium">remember</span>.
          </h2>
          <p className="mt-4 text-zinc-400 max-w-xl">
            We don't just deploy staff — we train them on your brand voice,
            your SOPs, and your guest journey. Every attendant is equipped with
            the Parking Boss app.
          </p>

          <ul className="mt-8 grid sm:grid-cols-2 gap-3">
            {perks.map((p) => (
              <li
                key={p}
                className="flex items-start gap-3 text-sm text-zinc-300"
                data-testid={`manpower-perk-${p.toLowerCase().replace(/\s+/g, "-").slice(0, 40)}`}
              >
                <Check size={18} className="text-[#D4AF37] shrink-0 mt-0.5" />
                {p}
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-6 grid grid-cols-6 gap-3">
          <div className="col-span-4 row-span-2 pb-card overflow-hidden relative min-h-[320px]">
            <img
              src="https://images.unsplash.com/photo-1578030637723-e291d6e1cc1a?auto=format&fit=crop&w=1000&q=80"
              alt="Professional valet attendant"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
            <div className="absolute bottom-4 left-4">
              <div className="pb-eyebrow">Certified Team</div>
              <div className="font-display text-xl mt-1">
                Trained at Parking Boss Academy
              </div>
            </div>
          </div>
          <div className="col-span-2 pb-card p-5">
            <div className="pb-gold-text font-display text-3xl">1,200+</div>
            <div className="text-xs text-zinc-400 mt-1 uppercase tracking-[0.18em]">
              On-field valets
            </div>
          </div>
          <div className="col-span-2 pb-card p-5">
            <div className="pb-gold-text font-display text-3xl">4.8★</div>
            <div className="text-xs text-zinc-400 mt-1 uppercase tracking-[0.18em]">
              Guest rating
            </div>
          </div>
          <div className="col-span-6 pb-card p-5">
            <div className="pb-gold-text font-display text-3xl">&lt; 48 hrs</div>
            <div className="text-xs text-zinc-400 mt-1 uppercase tracking-[0.18em]">
              Site deployment SLA
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ----- Industries marquee ----- */

const Industries = () => {
  const items = [
    { icon: Building2, t: "Luxury Hotels" },
    { icon: ShoppingBag, t: "Malls & Retail" },
    { icon: Hospital, t: "Hospitals" },
    { icon: Plane, t: "Airports" },
    { icon: UtensilsCrossed, t: "Fine Dining" },
    { icon: CalendarDays, t: "Events & Weddings" },
    { icon: Car, t: "Private Clubs" },
    { icon: Building2, t: "Corporate Parks" },
  ];
  const row = [...items, ...items];
  return (
    <section
      id="industries"
      className="relative py-20 border-t border-white/10 overflow-hidden"
      data-testid="industries-section"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 mb-10">
        <div className="pb-eyebrow">Industries Served</div>
        <h2 className="font-display mt-3 text-2xl md:text-4xl font-light">
          From <span className="pb-gold-text font-medium">5-star arrivals</span>{" "}
          to <span className="pb-gold-text font-medium">high-volume events</span>.
        </h2>
      </div>
      <div
        className="relative"
        style={{
          maskImage:
            "linear-gradient(to right, transparent, black 12%, black 88%, transparent)",
        }}
      >
        <div className="pb-marquee-track py-4">
          {row.map((it, i) => (
            <div
              key={i}
              className="flex items-center gap-3 px-6 py-4 border border-white/10 rounded-full bg-white/[0.02]"
            >
              <it.icon size={18} className="text-[#D4AF37]" />
              <span className="text-sm text-zinc-300 whitespace-nowrap">
                {it.t}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ----- Why us / testimonials ----- */

const WhyUs = () => {
  const points = [
    {
      t: "One vendor, zero handoffs",
      d: "Software, hardware and manpower under a single SLA. No finger-pointing.",
    },
    {
      t: "Deployed in 14 days",
      d: "Plug-and-play hardware, cloud software and pre-trained teams.",
    },
    {
      t: "Proven at scale",
      d: "2.4M+ vehicles managed. 99.98% platform uptime. Enterprise ready.",
    },
  ];
  const quotes = [
    {
      q: "Parking Boss turned our lobby from a bottleneck into a first impression. Retrieval times dropped by 58% in the first month.",
      a: "Rohan Kapoor",
      r: "GM · Grand Meridian Mumbai",
    },
    {
      q: "The single dashboard across 14 malls is a game-changer for our ops team. We finally see revenue per bay in real time.",
      a: "Priya Iyer",
      r: "VP Ops · Orion Mall Group",
    },
    {
      q: "Their on-ground supervisors are an extension of our hospitality team. Guests think they're ours.",
      a: "Aditya Mehra",
      r: "Director · Ivory Hospitality",
    },
  ];
  return (
    <section
      className="relative py-24 md:py-32 border-t border-white/10"
      data-testid="why-section"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="pb-eyebrow">Why Parking Boss</div>
          <h2 className="font-display mt-3 text-3xl md:text-5xl font-light leading-tight">
            One partner. <br />
            <span className="pb-gold-text font-medium">Every arrival.</span>
          </h2>
          <div className="mt-10 space-y-6">
            {points.map((p) => (
              <div
                key={p.t}
                className="flex gap-4"
                data-testid={`why-point-${p.t.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="h-10 w-10 rounded-full border border-[#D4AF37]/40 flex items-center justify-center shrink-0">
                  <Check size={18} className="text-[#D4AF37]" />
                </div>
                <div>
                  <div className="font-display text-xl">{p.t}</div>
                  <div className="text-zinc-400 text-sm mt-1">{p.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7 grid gap-5">
          {quotes.map((q, i) => (
            <figure
              key={i}
              className="pb-card p-7"
              data-testid={`testimonial-${i}`}
            >
              <div className="flex gap-1 text-[#D4AF37] mb-3">
                {[0, 1, 2, 3, 4].map((s) => (
                  <Star key={s} size={14} fill="currentColor" strokeWidth={0} />
                ))}
              </div>
              <blockquote className="font-display text-lg md:text-xl leading-relaxed font-light">
                "{q.q}"
              </blockquote>
              <figcaption className="mt-4 text-sm">
                <span className="text-white">{q.a}</span>
                <span className="text-zinc-500"> · {q.r}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ----- Contact / demo form ----- */

const ContactSection = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    industry: "",
    venues: "",
    message: "",
  });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", msg: "" });
    try {
      await axios.post(`${API}/demo-request`, form);
      setStatus({
        state: "success",
        msg: "Thank you! A specialist will reach out within 24 hours.",
      });
      setForm({
        name: "",
        email: "",
        company: "",
        phone: "",
        industry: "",
        venues: "",
        message: "",
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      setStatus({ state: "error", msg: String(detail) });
    }
  };

  return (
    <section
      id="contact"
      className="relative py-24 md:py-32 border-t border-white/10"
      data-testid="contact-section"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="pb-eyebrow">Request a Demo</div>
          <h2 className="font-display mt-3 text-3xl md:text-5xl font-light leading-tight">
            Let's design <br />
            your <span className="pb-gold-text font-medium">arrival experience</span>.
          </h2>
          <p className="mt-4 text-zinc-400">
            Tell us about your venue. We'll respond within 24 hours with a
            tailored proposal — software, hardware, and on-site team.
          </p>
          <div className="mt-10 space-y-4 text-sm">
            <a
              href="mailto:support@parkingboss.in"
              className="flex items-center gap-3 text-zinc-300 hover:text-white"
              data-testid="contact-email"
            >
              <Mail size={16} className="text-[#D4AF37]" />
              support@parkingboss.in
            </a>
            <a
              href="tel:+919633441888"
              className="flex items-center gap-3 text-zinc-300 hover:text-white"
              data-testid="contact-phone"
            >
              <Phone size={16} className="text-[#D4AF37]" />
              +91-9633441888
            </a>
            <div className="flex items-center gap-3 text-zinc-300">
              <MapPinned size={16} className="text-[#D4AF37]" />
              Operating across 12 countries
            </div>
          </div>
        </div>

        <form
          onSubmit={submit}
          className="lg:col-span-7 pb-card p-8 space-y-5"
          data-testid="demo-form"
        >
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="pb-eyebrow">Full Name</label>
              <input
                required
                value={form.name}
                onChange={update("name")}
                className="pb-input mt-2"
                placeholder="Jane Doe"
                data-testid="form-input-name"
              />
            </div>
            <div>
              <label className="pb-eyebrow">Work Email</label>
              <input
                required
                type="email"
                value={form.email}
                onChange={update("email")}
                className="pb-input mt-2"
                placeholder="jane@venue.com"
                data-testid="form-input-email"
              />
            </div>
            <div>
              <label className="pb-eyebrow">Company / Venue</label>
              <input
                required
                value={form.company}
                onChange={update("company")}
                className="pb-input mt-2"
                placeholder="The Grand Meridian"
                data-testid="form-input-company"
              />
            </div>
            <div>
              <label className="pb-eyebrow">Phone</label>
              <input
                value={form.phone}
                onChange={update("phone")}
                className="pb-input mt-2"
                placeholder="+91 90000 00000"
                data-testid="form-input-phone"
              />
            </div>
            <div>
              <label className="pb-eyebrow">Industry</label>
              <select
                value={form.industry}
                onChange={update("industry")}
                className="pb-input mt-2"
                data-testid="form-input-industry"
              >
                <option value="">Select industry</option>
                <option>Hotel</option>
                <option>Mall / Retail</option>
                <option>Hospital</option>
                <option>Airport</option>
                <option>Restaurant</option>
                <option>Events</option>
                <option>Other</option>
              </select>
            </div>
            <div>
              <label className="pb-eyebrow"># of venues</label>
              <select
                value={form.venues}
                onChange={update("venues")}
                className="pb-input mt-2"
                data-testid="form-input-venues"
              >
                <option value="">Select range</option>
                <option>1</option>
                <option>2–5</option>
                <option>6–15</option>
                <option>16+</option>
              </select>
            </div>
          </div>
          <div>
            <label className="pb-eyebrow">What would you like to solve?</label>
            <textarea
              value={form.message}
              onChange={update("message")}
              rows={4}
              className="pb-input mt-2 resize-none"
              placeholder="Tell us about your current valet setup, pain points, and goals..."
              data-testid="form-input-message"
            />
          </div>

          <div className="flex flex-wrap items-center gap-4 pt-2">
            <button
              type="submit"
              className="pb-btn-primary"
              disabled={status.state === "loading"}
              data-testid="form-submit-button"
            >
              {status.state === "loading" ? "Sending…" : "Request my demo"}
              <ArrowRight size={16} />
            </button>
            {status.state === "success" && (
              <span
                className="text-sm text-emerald-400"
                data-testid="form-success-message"
              >
                {status.msg}
              </span>
            )}
            {status.state === "error" && (
              <span
                className="text-sm text-red-400"
                data-testid="form-error-message"
              >
                {status.msg}
              </span>
            )}
          </div>
        </form>
      </div>
    </section>
  );
};

/* ----- Footer ----- */

const Footer = () => {
  return (
    <footer className="border-t border-white/10 bg-[#050505]" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-6 md:px-10 py-14 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="flex items-center">
            <img
              src="/parking-boss-logo.png"
              alt="Parking Boss"
              className="h-14 w-auto object-contain"
            />
          </div>
          <p className="mt-4 text-zinc-500 text-sm max-w-sm">
            Smart valet parking software, hardware and manpower for the world's
            most discerning venues.
          </p>
        </div>
        <div className="md:col-span-2">
          <div className="pb-eyebrow">Product</div>
          <ul className="mt-4 space-y-2 text-sm text-zinc-400">
            <li><a href="#software" className="hover:text-white">Software</a></li>
            <li><a href="#hardware" className="hover:text-white">Hardware</a></li>
            <li><a href="#manpower" className="hover:text-white">Manpower</a></li>
            <li><a href="#solutions" className="hover:text-white">Solutions</a></li>
          </ul>
        </div>
        <div className="md:col-span-2">
          <div className="pb-eyebrow">Company</div>
          <ul className="mt-4 space-y-2 text-sm text-zinc-400">
            <li><a href="#industries" className="hover:text-white">Industries</a></li>
            <li><a href="#contact" className="hover:text-white">Contact</a></li>
            <li><a href="#contact" className="hover:text-white">Careers</a></li>
          </ul>
        </div>
        <div className="md:col-span-3">
          <div className="pb-eyebrow">Get in touch</div>
          <ul className="mt-4 space-y-2 text-sm text-zinc-400">
            <li>support@parkingboss.in</li>
            <li>+91-9633441888</li>
            <li>Mon–Sat · 9am → 9pm IST</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="mx-auto max-w-7xl px-6 md:px-10 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-zinc-600">
          <div>© {new Date().getFullYear()} Parking Boss. All rights reserved.</div>
          <div className="flex gap-5">
            <a href="#" className="hover:text-zinc-300">Privacy</a>
            <a href="#" className="hover:text-zinc-300">Terms</a>
            <a href="#" className="hover:text-zinc-300">Security</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

/* ------------------------------------------------------------------ */
/*  App                                                                */
/* ------------------------------------------------------------------ */

function App() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/stats`)
      .then((res) => setStats(res.data))
      .catch(() => {});
  }, []);

  const scrollToDemo = () => {
    const el = document.getElementById("contact");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="App">
      <Header onDemoClick={scrollToDemo} />
      <Hero onDemoClick={scrollToDemo} stats={stats} />
      <Solutions />
      <SoftwareSection onDemoClick={scrollToDemo} />
      <HardwareSection />
      <ManpowerSection />
      <Industries />
      <WhyUs />
      <ContactSection />
      <Footer />
    </div>
  );
}

export default App;
